import classNames from "classnames";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import Button from "~/components/common/button";
import { useConfiguration } from "~/contexts/configuration";
import {
  KikReviewsTranslateV2ResponseItem,
  KikReviewsV2ResponseReviewItem,
} from "~/shared/power-reviews-response.interface";
import powerReviewUtils from "~/utils/power-reviews-utils";

import styles from "./review-item-text.module.scss";
import ReviewStars from "./review-stars";
import ReviewTimeAgo from "./review-time-ago";

type PropsProductReviews = {
  review: KikReviewsV2ResponseReviewItem;
  inDialog?: boolean;
};

export default function ReviewItemText(props: PropsProductReviews) {
  const t = useTranslations();
  const { review, inDialog } = props;
  const { organizationUnitId } = useConfiguration();
  const [translation, setTranslation] = useState<KikReviewsTranslateV2ResponseItem>();

  const router = useRouter();
  const currentLang = router.locale?.split("-")[0] ?? "en";

  const isDifferentLang = (reviewLocale: string) => {
    return currentLang.toUpperCase() !== reviewLocale.split("_")[1]?.toUpperCase();
  };

  const showTranslation = async () => {
    const translatedReview = await powerReviewUtils.getReviewTranslation(
      review.review_id.toString(),
      organizationUnitId,
      router.locale || "en"
    );
    if (translatedReview) {
      setTranslation(translatedReview);
    }
  };
  const translatableHeadline = useMemo(() => {
    if (translation) {
      return translation.translation_document.translated_headline;
    } else {
      return review.details.headline;
    }
  }, [translation, review]);

  const translatableComment = useMemo(() => {
    if (translation) {
      return translation.translation_document.translated_comments;
    } else if (inDialog) {
      return `"${review.details.comments}"`;
    } else {
      return review.details.comments;
    }
  }, [translation, inDialog, review]);

  return (
    <div
      className={classNames(styles.reviewItemContent, {
        [styles.inDialog]: inDialog,
      })}
    >
      <div className={styles.reviewsHeader}>
        <div className={styles.reviewsStarsRating}>
          <ReviewStars rating={review.metrics.rating} id={review.review_id} context="list" />
        </div>
        <div className={styles.reviewsHeadline} id={`review-headline-${review.review_id}`}>
          {translatableHeadline}
        </div>
      </div>
      <div className={styles.reviewsDescription}>{translatableComment}</div>
      <ReviewTimeAgo timestamp={review.details.created_date} display={inDialog ? undefined : "absolute"} />
      <div className={styles.reviewsAuthor}>
        <span className={styles.reviewsAuthorNickname}>{review.details.nickname}</span>
        <span className={styles.reviewsAuthorLocation}>{review.details.location}</span>
        {review.badges.is_verified_buyer && (
          <span className={styles.reviewsAuthorVerified}>{t("reviews.verifiedBuyer")}</span>
        )}
      </div>
      {!inDialog && isDifferentLang(review.details.locale) && (
        <Button className={styles.reviewsShowTranslation} onClick={showTranslation}>
          {t("reviews.showTranslation")}
        </Button>
      )}
    </div>
  );
}
